import { useEffect, useState, useMemo, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import {
    Box, Typography, Stack, Chip, FormControl, InputLabel, Select, MenuItem, Button, TextField,
    Table, TableBody, TableContainer, TableHead, TableRow, Paper, Tooltip, IconButton, Pagination,
    Dialog, DialogTitle, DialogContent, DialogActions, RadioGroup, FormControlLabel, Radio, CircularProgress, Checkbox
} from '@mui/material';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import LinkOutlinedIcon from '@mui/icons-material/Link';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { toast } from 'react-toastify';
import { setMainHeading } from '../../../../redux/modules/mainHeading/slice';
import {
    getUnmatchedResultsRequest, assignUnmatchedResultRequest, discardUnmatchedResultRequest,
    batchAssignUnmatchedResultRequest, resetBatchAssignUnmatchedResult,
    resetAssignUnmatchedResult, resetDiscardUnmatchedResult, resetGetUnmatchedResults,
} from '../../../../redux/modules/laboratory/labUnmatchedResult/slice';
import { getLabOrderRequest } from '../../../../redux/modules/laboratory/labOrder/slice';
import { getAllLabAnalyzerRequest } from '../../../../redux/modules/laboratory/labAnalyzer/slice';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: { backgroundColor: theme.palette.primary.main, color: theme.palette.common.white },
    [`&.${tableCellClasses.body}`]: { fontSize: 14 },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
    '&:last-child td, &:last-child th': { border: 0 },
}));

const reasonLabels = {
    order_not_found: 'No matching order',
    no_sample_id: 'No sample id sent',
    sequence_rebuilt: 'Auto-matched from sample id',
    test_not_on_order: 'Test not on the matched order',
    result_signed_off: 'Re-sent after signoff — amend the released result',
    processing_error: 'Processing error — retry assign',
};

export default function LabUnmatchedResults() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const basePath = '/' + location.pathname.split('/')[1];

    const { getRes, assignRes, batchAssignRes, discardRes, loading } = useSelector(state => state.labUnmatchedResult);
    const orderState = useSelector(state => state.labOrder);
    const analyzersRes = useSelector(state => state.labAnalyzer?.getAllRes);

    const LIMIT = 25;
    const [statusFilter, setStatusFilter] = useState('pending');
    const [analyzerFilter, setAnalyzerFilter] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [page, setPage] = useState(1);
    const [assignDialog, setAssignDialog] = useState({ open: false, row: null, batch: false });
    const [selectedIds, setSelectedIds] = useState([]);
    const [expandedGroups, setExpandedGroups] = useState({});
    const [discardDialog, setDiscardDialog] = useState({ open: false, row: null, reason: '' });
    const [orderSearch, setOrderSearch] = useState('');
    const [selectedOrderId, setSelectedOrderId] = useState('');

    // Date pickers give a local calendar day; send the local start/end of that day
    // as instants so "date" matches the viewer's timezone, not the API server's.
    const dayStartISO = (d) => (d ? new Date(`${d}T00:00:00`).toISOString() : '');
    const dayEndISO = (d) => (d ? new Date(`${d}T23:59:59.999`).toISOString() : '');

    const refetch = () => dispatch(getUnmatchedResultsRequest({
        status: statusFilter,
        analyzerId: analyzerFilter,
        dateFrom: dayStartISO(dateFrom),
        dateTo: dayEndISO(dateTo),
        page,
        // Pending is shown grouped by sample-run, so pull a full working set (not a
        // 25-row page) — otherwise a run's analytes could split across pages and a
        // group would be incomplete. Assigned/discarded stay paginated.
        limit: statusFilter === 'pending' ? 200 : LIMIT,
    }));

    useEffect(() => {
        dispatch(setMainHeading('Unmatched Analyzer Results'));
        dispatch(getAllLabAnalyzerRequest());
        return () => { dispatch(resetGetUnmatchedResults()); };
    }, [dispatch]);

    // Refetch on any filter or page change. Filter handlers reset the page to 1,
    // so a filter change and its page reset batch into a single fetch. Selection
    // is per-visible-page, so clear it whenever the visible rows change.
    useEffect(() => { refetch(); setSelectedIds([]); setExpandedGroups({}); }, [dispatch, statusFilter, analyzerFilter, dateFrom, dateTo, page]);

    useEffect(() => {
        if (!assignRes) return;
        if (assignRes.success) {
            toast('Result assigned to order');
            setAssignDialog({ open: false, row: null });
            setSelectedOrderId('');
            setOrderSearch('');
            refetch();
        } else {
            toast(assignRes.message || 'Assign failed');
        }
        dispatch(resetAssignUnmatchedResult());
    }, [assignRes]);

    useEffect(() => {
        if (!batchAssignRes) return;
        if (batchAssignRes.success) {
            toast(batchAssignRes.message || 'Results assigned to order');
            setAssignDialog({ open: false, row: null, batch: false });
            setSelectedOrderId('');
            setOrderSearch('');
            setSelectedIds([]);
            refetch();
        } else {
            toast(batchAssignRes.message || 'Batch assign failed');
        }
        dispatch(resetBatchAssignUnmatchedResult());
    }, [batchAssignRes]);

    useEffect(() => {
        if (!discardRes) return;
        if (discardRes.success) {
            toast('Unmatched result discarded');
            setDiscardDialog({ open: false, row: null, reason: '' });
            refetch();
        } else {
            toast(discardRes.message || 'Discard failed');
        }
        dispatch(resetDiscardUnmatchedResult());
    }, [discardRes]);

    const openAssign = (row) => {
        setSelectedOrderId('');
        setOrderSearch('');
        dispatch(getLabOrderRequest({ page: 1, limit: 8, searchQuery: '' }));
        setAssignDialog({ open: true, row, batch: false });
    };

    // ids optional: a group's "Assign all" passes its member ids; the toolbar
    // "Assign selected" passes nothing and falls back to the checkbox selection.
    const openBatchAssign = (ids) => {
        const list = Array.isArray(ids) ? ids : selectedIds;
        if (list.length === 0) { toast('Select at least one result'); return; }
        setSelectedOrderId('');
        setOrderSearch('');
        dispatch(getLabOrderRequest({ page: 1, limit: 8, searchQuery: '' }));
        setAssignDialog({ open: true, row: null, batch: true, ids: list });
    };

    const toggleOne = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    const toggleAll = () => {
        const pageIds = rows.map(r => r._id);
        const allSelected = pageIds.length > 0 && pageIds.every(id => selectedIds.includes(id));
        setSelectedIds(allSelected ? [] : pageIds);
    };

    const searchOrders = (q) => {
        setOrderSearch(q);
        dispatch(getLabOrderRequest({ page: 1, limit: 8, searchQuery: q }));
    };

    const confirmAssign = () => {
        if (!selectedOrderId) { toast('Select an order'); return; }
        if (assignDialog.batch) {
            dispatch(batchAssignUnmatchedResultRequest({ ids: assignDialog.ids || selectedIds, labOrderId: selectedOrderId }));
        } else {
            dispatch(assignUnmatchedResultRequest({ id: assignDialog.row._id, labOrderId: selectedOrderId }));
        }
    };

    const confirmDiscard = () => {
        dispatch(discardUnmatchedResultRequest({ id: discardDialog.row._id, reason: discardDialog.reason }));
    };

    const rows = getRes?.data || [];
    const orders = orderState?.getRes?.data || [];
    const dataCount = getRes?.dataCount || 0;
    const pageCount = Math.ceil(dataCount / LIMIT);

    // Pending queue grouped by sample-run (analyzer + sample id + run day): a CBC
    // that arrived as ~20 analytes collapses to ONE expandable row, so a tech can
    // link the whole run to an order in one action instead of ticking each analyte.
    const groups = useMemo(() => {
        const map = new Map();
        for (const r of rows) {
            const key = `${r.analyzerId?._id || r.analyzerId || ''}|${r.sampleId || ''}|${r.sampleDay || ''}`;
            let g = map.get(key);
            if (!g) {
                g = { key, analyzerName: r.analyzerId?.name || r.analyzerName || '-', sampleId: r.sampleId || '', members: [], receivedAt: r.createdAt };
                map.set(key, g);
            }
            g.members.push(r);
            if (r.createdAt && (!g.receivedAt || new Date(r.createdAt) < new Date(g.receivedAt))) g.receivedAt = r.createdAt;
        }
        return [...map.values()];
    }, [rows]);

    const toggleGroup = (key) => setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));
    const groupAllSelected = (g) => g.members.length > 0 && g.members.every(m => selectedIds.includes(m._id));
    const groupSomeSelected = (g) => g.members.some(m => selectedIds.includes(m._id));
    const toggleGroupSelect = (g) => {
        const ids = g.members.map(m => m._id);
        const all = groupAllSelected(g);
        setSelectedIds(prev => all ? prev.filter(x => !ids.includes(x)) : [...new Set([...prev, ...ids])]);
    };

    // One analyte row — shared by the grouped pending view and the flat
    // assigned/discarded views (the per-status columns are already conditional).
    const renderMemberRow = (row) => (
        <StyledTableRow key={row._id} selected={selectedIds.includes(row._id)}>
            {statusFilter === 'pending' && (
                <StyledTableCell padding="checkbox">
                    <Checkbox size="small" checked={selectedIds.includes(row._id)} onChange={() => toggleOne(row._id)} />
                </StyledTableCell>
            )}
            <StyledTableCell sx={{ whiteSpace: 'nowrap' }}>
                {row.createdAt ? new Date(row.createdAt).toLocaleString('en-GB') : '-'}
            </StyledTableCell>
            <StyledTableCell>{row.analyzerId?.name || row.analyzerName || '-'}</StyledTableCell>
            <StyledTableCell>
                {row.lisTestId?.name || row.lisTestName || '-'}
                <Typography variant="caption" display="block" color="text.secondary">
                    sent as {row.analyzerTestName || row.analyzerTestCode || '?'}
                </Typography>
            </StyledTableCell>
            <StyledTableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                {row.value}{row.unit ? ` ${row.unit}` : ''}
            </StyledTableCell>
            <StyledTableCell>{row.sampleId || <em>(none)</em>}</StyledTableCell>
            <StyledTableCell>
                <Chip label={reasonLabels[row.reason] || row.reason} size="small" variant="outlined" />
            </StyledTableCell>
            {statusFilter === 'assigned' && (
                <StyledTableCell>
                    {row.resolvedToOrderId ? (
                        <>
                            {`${row.resolvedToOrderId.patientId?.fName || ''} ${row.resolvedToOrderId.patientId?.lName || ''}`.trim() || '-'}
                            <Typography variant="caption" display="block" color="text.secondary">
                                {row.resolvedToOrderId.specimen?.accessionNumber || row.resolvedToOrderId.labOrderId}
                                {' · '}
                                {row.resolvedBy ? 'assigned by a tech' : 'auto-matched'}
                            </Typography>
                        </>
                    ) : '-'}
                </StyledTableCell>
            )}
            {statusFilter !== 'discarded' && (
                <StyledTableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title={statusFilter === 'assigned' ? 'Reassign to a different order' : 'Assign to order'}>
                        <IconButton color="primary" size="small" onClick={() => openAssign(row)}>
                            <LinkOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    {statusFilter === 'pending' && (
                        <Tooltip title="Discard">
                            <IconButton color="error" size="small" onClick={() => setDiscardDialog({ open: true, row, reason: '' })}>
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </StyledTableCell>
            )}
        </StyledTableRow>
    );

    return (
        <>
            <Box mt={4} mb={2}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Unmatched Analyzer Results</Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                    <strong>Pending</strong> — results an analyzer sent that matched no order (no/disabled barcode, or a
                    mistyped sample id). Assign each to the correct order, or discard it.
                    {' '}
                    <strong>Assigned</strong> — includes results auto-matched by rebuilding the accession from a short
                    keyed sample id. Check the patient is right; if the number was mis-keyed, reassign it and the result
                    moves to the correct order, withdrawn from the wrong one.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap alignItems={{ sm: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Status</InputLabel>
                        <Select label="Status" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="assigned">Assigned</MenuItem>
                            <MenuItem value="discarded">Discarded</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Analyzer</InputLabel>
                        <Select label="Analyzer" value={analyzerFilter} onChange={(e) => { setAnalyzerFilter(e.target.value); setPage(1); }}>
                            <MenuItem value="">All analyzers</MenuItem>
                            {(analyzersRes?.data || []).map(a => (
                                <MenuItem key={a._id} value={a._id}>{a.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
                        value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                    />
                    <TextField
                        size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
                        value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                    />
                    {(analyzerFilter || dateFrom || dateTo) && (
                        <Button size="small" onClick={() => { setAnalyzerFilter(''); setDateFrom(''); setDateTo(''); setPage(1); }}>
                            Clear
                        </Button>
                    )}
                </Stack>
            </Box>

            {statusFilter === 'pending' && selectedIds.length > 0 && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedIds.length} selected</Typography>
                    <Button size="small" variant="contained" startIcon={<LinkOutlinedIcon />} onClick={openBatchAssign}>
                        Assign selected to order
                    </Button>
                    <Button size="small" onClick={() => setSelectedIds([])}>Clear</Button>
                </Stack>
            )}

            <Box sx={{ overflowX: { xs: 'auto', md: 'visible' } }}>
                <TableContainer component={Paper} className="tableShadow">
                    <Table sx={{ minWidth: { xs: 700, md: 900 } }} className="commonTable">
                        <TableHead>
                            <TableRow>
                                {statusFilter === 'pending' && (
                                    <StyledTableCell padding="checkbox">
                                        <Checkbox
                                            size="small"
                                            sx={{ color: 'white', '&.Mui-checked': { color: 'white' }, '&.MuiCheckbox-indeterminate': { color: 'white' } }}
                                            checked={rows.length > 0 && rows.every(r => selectedIds.includes(r._id))}
                                            indeterminate={selectedIds.length > 0 && !rows.every(r => selectedIds.includes(r._id))}
                                            onChange={toggleAll}
                                        />
                                    </StyledTableCell>
                                )}
                                <StyledTableCell>Received</StyledTableCell>
                                <StyledTableCell>Analyzer</StyledTableCell>
                                <StyledTableCell>Test</StyledTableCell>
                                <StyledTableCell>Result</StyledTableCell>
                                <StyledTableCell>Sample ID sent</StyledTableCell>
                                <StyledTableCell>Reason</StyledTableCell>
                                {statusFilter === 'assigned' && <StyledTableCell>Assigned to</StyledTableCell>}
                                {statusFilter !== 'discarded' && <StyledTableCell align="right">Action</StyledTableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 ? (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={statusFilter === 'discarded' ? 6 : 8}>
                                        <Typography color="text.secondary">
                                            {loading ? 'Loading…' : `No ${statusFilter} unmatched results.`}
                                        </Typography>
                                    </StyledTableCell>
                                </StyledTableRow>
                            ) : statusFilter === 'pending' ? (
                                groups.map(g => (
                                    <Fragment key={g.key}>
                                        <StyledTableRow sx={{ backgroundColor: 'action.selected' }}>
                                            <StyledTableCell colSpan={8}>
                                                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                                                    <IconButton size="small" onClick={() => toggleGroup(g.key)} aria-label="expand sample group">
                                                        {expandedGroups[g.key] ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                                                    </IconButton>
                                                    <Checkbox
                                                        size="small"
                                                        checked={groupAllSelected(g)}
                                                        indeterminate={groupSomeSelected(g) && !groupAllSelected(g)}
                                                        onChange={() => toggleGroupSelect(g)}
                                                    />
                                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                        {g.sampleId ? `Sample ${g.sampleId}` : 'No sample id'}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        · {g.analyzerName} · {g.members.length} result{g.members.length > 1 ? 's' : ''}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                        {g.members.slice(0, 6).map(m => (
                                                            <Chip key={m._id} label={m.lisTestId?.testCode || m.lisTestName || m.lisTestCode || '?'} size="small" variant="outlined" />
                                                        ))}
                                                        {g.members.length > 6 && <Chip label={`+${g.members.length - 6}`} size="small" />}
                                                    </Box>
                                                    <Box sx={{ flexGrow: 1 }} />
                                                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                                        {g.receivedAt ? new Date(g.receivedAt).toLocaleString('en-GB') : ''}
                                                    </Typography>
                                                    <Button size="small" variant="contained" startIcon={<LinkOutlinedIcon />} onClick={() => openBatchAssign(g.members.map(m => m._id))}>
                                                        Assign all to order
                                                    </Button>
                                                </Stack>
                                            </StyledTableCell>
                                        </StyledTableRow>
                                        {expandedGroups[g.key] && g.members.map(renderMemberRow)}
                                    </Fragment>
                                ))
                            ) : (
                                rows.map(renderMemberRow)
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {statusFilter !== 'pending' && pageCount > 1 && (
                <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} mt={2}>
                    <Typography variant="body2" color="text.secondary">{dataCount} result(s)</Typography>
                    <Pagination count={pageCount} page={page} onChange={(e, v) => setPage(v)} color="primary" />
                </Stack>
            )}
            {statusFilter === 'pending' && dataCount > rows.length && (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
                    Showing the first {rows.length} of {dataCount} pending results — narrow with the analyzer / date filters to see the rest.
                </Typography>
            )}

            {/* Assign dialog */}
            <Dialog open={assignDialog.open} onClose={() => setAssignDialog({ open: false, row: null })} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {assignDialog.batch
                        ? `Assign ${(assignDialog.ids || selectedIds).length} results to an order`
                        : assignDialog.row?.status === 'assigned'
                            ? 'Reassign result to a different order'
                            : 'Assign result to an order'}
                </DialogTitle>
                <DialogContent>
                    {assignDialog.batch && (
                        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                            <Typography variant="body2">
                                Assigning <strong>{(assignDialog.ids || selectedIds).length}</strong> result(s) to the chosen order.
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Each is added to the order as its own test result; any that can't be matched are reported back.
                            </Typography>
                        </Box>
                    )}
                    {assignDialog.row && (
                        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                            <Typography variant="body2">
                                <strong>{assignDialog.row.lisTestId?.name || assignDialog.row.lisTestName}</strong>{' '}
                                = {assignDialog.row.value}{assignDialog.row.unit ? ` ${assignDialog.row.unit}` : ''}
                                {' '}from {assignDialog.row.analyzerId?.name || assignDialog.row.analyzerName}
                            </Typography>
                            {assignDialog.row.status === 'assigned' && assignDialog.row.resolvedToOrderId && (
                                <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
                                    Currently on{' '}
                                    <strong>
                                        {`${assignDialog.row.resolvedToOrderId.patientId?.fName || ''} ${assignDialog.row.resolvedToOrderId.patientId?.lName || ''}`.trim() || assignDialog.row.resolvedToOrderId.labOrderId}
                                    </strong>
                                    . Reassigning withdraws it from that order and writes it on the one you pick.
                                </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">
                                Sample id sent: {assignDialog.row.sampleId || '(none)'}
                            </Typography>
                        </Box>
                    )}
                    <TextField
                        fullWidth size="small" autoFocus
                        label="Search orders (accession, patient, order #)"
                        value={orderSearch}
                        onChange={(e) => searchOrders(e.target.value)}
                        sx={{ mb: 1 }}
                    />
                    <Box sx={{ maxHeight: 280, overflow: 'auto' }}>
                        {orderState?.loading ? (
                            <Stack alignItems="center" py={3}><CircularProgress size={22} /></Stack>
                        ) : orders.length > 0 ? (
                            <RadioGroup value={selectedOrderId} onChange={(e) => setSelectedOrderId(e.target.value)}>
                                {orders.map(o => (
                                    <FormControlLabel
                                        key={o._id} value={o._id} control={<Radio size="small" />}
                                        sx={{ alignItems: 'flex-start', mb: 0.5 }}
                                        label={
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {o.labOrderId} · {o.patientId ? `${o.patientId.fName || ''} ${o.patientId.lName || ''}`.trim() : '-'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {o.specimen?.accessionNumber ? `Acc ${o.specimen.accessionNumber} · ` : ''}
                                                    status {o.status}
                                                    {o.tests?.length ? ` · ${o.tests.map(t => t.testId?.testCode).filter(Boolean).slice(0, 4).join(', ')}` : ''}
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                ))}
                            </RadioGroup>
                        ) : (
                            <Typography variant="body2" color="text.secondary" py={2}>No orders found.</Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAssignDialog({ open: false, row: null })}>Cancel</Button>
                    <Button variant="contained" onClick={confirmAssign} disabled={!selectedOrderId || loading}>Assign</Button>
                </DialogActions>
            </Dialog>

            {/* Discard dialog */}
            <Dialog open={discardDialog.open} onClose={() => setDiscardDialog({ open: false, row: null, reason: '' })} maxWidth="xs" fullWidth>
                <DialogTitle>Discard unmatched result</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Discard this result (e.g. a QC or test sample). It will not be added to any order.
                    </Typography>
                    <TextField
                        fullWidth size="small" label="Reason (optional)"
                        value={discardDialog.reason}
                        onChange={(e) => setDiscardDialog(d => ({ ...d, reason: e.target.value }))}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDiscardDialog({ open: false, row: null, reason: '' })}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={confirmDiscard} disabled={loading}>Discard</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
